import { firestore } from '../firebase';
import {
    getFirestore,
    addDoc,
    collection,
    serverTimestamp,
    query,
    where,
    getDocs,
    doc,
    setDoc,
    updateDoc,
    writeBatch
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";

export interface ArtistInfo {
    artistName: string;
    email: string;
}

export type MusicfetchMode = "url" | "isrc" | "upc";

export interface MusicLinksResult {
    name?: string;
    type?: string;
    coverArtUrl?: string;
    previewUrl?: string;
    releaseDate?: string;
    genres?: string[];
    isrc?: string;
    upc?: string;
    links: {
        amazonMusic?: string;
        appleMusic?: string;
        soundcloud?: string;
        spotify?: string;
        tidal?: string;
        youtubeMusic?: string;
    };
}

export interface TrackLinkData {
    artistName: string;
    coverArtUrl: string;
    featureArtists: string[];
    genre: string;
    moodTags: string[];
    platformLinks: {
        amazonMusic?: string;
        appleMusic?: string;
        soundcloud?: string;
        spotify?: string;
        tidal?: string;
        youtubeMusic?: string;
    };
    regionID: number;
    stateID: number;
    releaseDate: Date;
    releaseType: string;
    trackTitle: string;
    userId: string;
}

export interface UserInfo {
    userId: string;
    artistName: string;
    email?: string;
    stateID?: number;
    regionID?: number;
}

export interface CreateUserData {
    artistName: string;
    name: string;
    userName: string;
    userType: "Artist";
    stateID: number;
    regionID: number;
    agreedToTerms: boolean;
    fans: number;
    profileClaimed: boolean;
    lastCheckedNotifications: Date;
}

type FetchArgs =
    | { mode: "url"; url: string; country?: string }
    | { mode: "isrc"; isrc: string; country?: string }
    | { mode: "upc"; upc: string; country?: string };

export const fetchMusicLinks = async (args: FetchArgs): Promise<MusicLinksResult> => {
    const functions = getFunctions();
    const musicfetchProxy = httpsCallable(functions, "musicfetchProxy");

    const resp = await musicfetchProxy(args as any);
    const data = (resp?.data ?? {}) as any;
    const slim = data.slim ?? data;

    const coverArtUrl: string | undefined = slim?.primaryImage?.url;

    return {
        name: slim?.name,
        type: slim?.type,
        coverArtUrl,
        previewUrl: slim?.previewUrl,
        releaseDate: slim?.releaseDate,
        genres: slim?.genres || [],
        isrc: slim?.isrc,
        upc: slim?.upc,
        links: {
            amazonMusic: slim?.links?.amazonMusic ?? "",
            appleMusic: slim?.links?.appleMusic ?? "",
            soundcloud: slim?.links?.soundcloud ?? "",
            spotify: slim?.links?.spotify ?? "",
            tidal: slim?.links?.tidal ?? "",
            youtubeMusic: slim?.links?.youtubeMusic ?? "",
        },
    };
};

export const fetchAllUsers = async (): Promise<UserInfo[]> => {
    const db = getFirestore();
    try {
        const usersCol = collection(db, 'users');
        const snapshot = await getDocs(usersCol);

        const users: UserInfo[] = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
                userId: doc.id,
                artistName: data.artistName || data.displayName || "Unknown Artist",
                email: data.email,
                stateID: data.stateID,
                regionID: data.regionID
            };
        });
        return users.sort((a, b) => a.artistName.localeCompare(b.artistName));
    } catch (error) {
        console.error("Error fetching users:", error);
        return [];
    }
};

export const shuffleStateCharts = async (): Promise<void> => {
    const functions = getFunctions();
    const shuffleFn = httpsCallable(functions, "shuffleStateCharts");
    await shuffleFn();
};

export const shuffleTop100Charts = async (): Promise<void> => {
    const functions = getFunctions();
    const shuffleFn = httpsCallable(functions, "shuffleTop100");
    await shuffleFn();
};

export const createTrackLinkDocument = async (data: TrackLinkData) => {
    const db = getFirestore();
    try {
        const docRef = await addDoc(collection(db, "trackLinks"), {
            ...data,
            releaseDate: data.releaseDate,
            createdAt: serverTimestamp(),
        });
        console.log("Track Link Document created with ID: ", docRef.id);
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error("Error creating track link document: ", error);
        throw error;
    }
};

export const createUserDocument = async (data: CreateUserData) => {
    const db = getFirestore();
    try {
        const docRef = await addDoc(collection(db, "users"), {
            ...data,
            registerDate: serverTimestamp(),
        });
        console.log("User Document created with ID: ", docRef.id);
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error("Error creating user document: ", error);
        throw error;
    }
};

export const getArtistImage = async (userId: string): Promise<string> => {
    try {
        const functions = getFunctions();
        const callable = httpsCallable(functions, "fetchArtistImage");

        const result = await callable({
            force: true,
            targetUserId: userId
        });

        const data = result.data as any;

        if (data?.avatarUrl) {
            return data.avatarUrl;
        } else {
            throw new Error("Function finished but returned no URL.");
        }
    } catch (error: any) {
        console.error("Error fetching artist image:", error);
        throw error;
    }
};

export const updateStateCharts = async (): Promise<void> => {
    const db = getFirestore();

    try {
        const chartsCol = collection(db, "stateCharts");
        const chartsSnap = await getDocs(chartsCol);

        if (chartsSnap.empty) {
            console.warn("No state charts found to update.");
            return;
        }

        const tracksCol = collection(db, "trackLinks");
        const tracksSnap = await getDocs(tracksCol);

        const tracksByState: Record<number, string[]> = {};

        tracksSnap.docs.forEach((doc) => {
            const data = doc.data();
            const sID = data.stateID;
            if (typeof sID === "number") {
                if (!tracksByState[sID]) tracksByState[sID] = [];
                tracksByState[sID].push(doc.id);
            }
        });

        const batch = writeBatch(db);
        let updateCount = 0;

        chartsSnap.docs.forEach((chartDoc) => {
            const chartData = chartDoc.data();
            const stateID = chartData.stateID;

            if (typeof stateID === "number") {
                const tracksForState = tracksByState[stateID] || [];

                for (let i = tracksForState.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [tracksForState[i], tracksForState[j]] = [
                        tracksForState[j],
                        tracksForState[i],
                    ];
                }

                const selectedIds = tracksForState.slice(0, 50);

                const ref = doc(db, "stateCharts", chartDoc.id);

                const dateString = new Date().toLocaleString("en-US", {
                    timeZone: "America/New_York",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                    timeZoneName: "short",
                });

                batch.update(ref, {
                    chartTrackIDs: selectedIds,
                    lastUpdated: dateString,
                    lastShuffledAt: dateString,
                });

                updateCount++;
            }
        });

        await batch.commit();
        console.log(`Bulk updated ${updateCount} state charts.`);

    } catch (error) {
        console.error("Error updating state charts:", error);
        throw error;
    }
};

const TOP_100_CHART_DOC_ID = "9gYzl8127rQ9kosWpRCy";

export const updateTop100Charts = async (): Promise<void> => {
    const db = getFirestore();

    try {
        const tracksCol = collection(db, "trackLinks");
        const snapshot = await getDocs(tracksCol);

        if (snapshot.empty) {
            console.warn("No tracks found in trackLinks.");
            return;
        }

        const allTrackIds = snapshot.docs.map((doc) => doc.id);

        for (let i = allTrackIds.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [allTrackIds[i], allTrackIds[j]] = [allTrackIds[j], allTrackIds[i]];
        }

        const selectedIds = allTrackIds.slice(0, 100);

        const chartRef = doc(db, "top100Chart", TOP_100_CHART_DOC_ID);

        await updateDoc(chartRef, {
            chartTrackIDs: selectedIds,
            lastUpdated: serverTimestamp(),
        });

        console.log(`Updated Top 100 Chart with ${selectedIds.length} tracks.`);
    } catch (error) {
        console.error("Error updating Top 100 charts:", error);
        throw error;
    }
};

const TRENDING_ARTISTS_DOC_ID = "HCWHd0QJl5piad2vDdvc";

export const updateTrendingArtists = async (): Promise<void> => {
    const db = getFirestore();

    try {
        const usersCol = collection(db, "users");
        const q = query(usersCol, where("userType", "==", "Artist"));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            console.warn("No artist users found to trend.");
            return;
        }

        const allUserIds = snapshot.docs.map((doc) => doc.id);

        for (let i = allUserIds.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [allUserIds[i], allUserIds[j]] = [allUserIds[j], allUserIds[i]];
        }

        const selectedIds = allUserIds.slice(0, 18);

        const trendingRef = doc(db, "trendingArtists", TRENDING_ARTISTS_DOC_ID);

        await setDoc(trendingRef, {
            artistIDs: selectedIds,
            dateUpdated: new Date().toLocaleString("en-US", {
                timeZone: "America/New_York",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                second: "numeric",
                timeZoneName: "short",
            }),
        });

        console.log(`Updated trending artists with ${selectedIds.length} IDs.`);
    } catch (error) {
        console.error("Error updating trending artists:", error);
        throw error;
    }
};

export const createNotificationDoc = async (message: string, type: "top_100" | "new_music" | "new_artists") => {
    const db = getFirestore();
    try {
        const docRef = await addDoc(collection(db, "notifications"), {
            message: message,
            type: type,
            origin: "system_generated",
            timestamp: serverTimestamp(),
        });
        console.log("Notification Document created with ID: ", docRef.id);
        return { success: true, id: docRef.id };
    } catch (error) {
        console.error("Error creating notification document: ", error);
        throw error;
    }
};